import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getClientId, getSessionId, trackEvent } from 'libs/ga4/ga4';
import { makeGa4ProductItem, toBooleanOrNull, toIntegerOrNull, toNumberOrNull, toStringOrNull } from 'libs/ga4/helpers';

export const Ga4Context = createContext(null);

export const Ga4IdentityContext = createContext(null);

const EmptyIdentity = Object.freeze({ clientId: null, sessionId: null });

const visibilityUpgradeView = ({ listingId, source, sellerType, categoryId }) =>
  trackEvent('visibility_upgrade_view', {
    listing_id: toStringOrNull(listingId),
    source: toStringOrNull(source),
    seller_type: toStringOrNull(sellerType),
    category_id: toStringOrNull(categoryId),
  });

const visibilityUpgradeSelect = ({ listingId, upgradeType, dailyPrice, durationDays, source, sellerType }) =>
  trackEvent('visibility_upgrade_select', {
    listing_id: toStringOrNull(listingId),
    upgrade_type: toStringOrNull(upgradeType),
    daily_price: toNumberOrNull(dailyPrice),
    duration_days: toIntegerOrNull(durationDays),
    source: toStringOrNull(source),
    seller_type: toStringOrNull(sellerType),
  });

const balanceTopupStart = (topupAmount) => trackEvent('balance_topup_start', { topup_amount: toIntegerOrNull(topupAmount) });

const balanceTopupMethodSelect = ({ topupAmount, paymentMethod }) =>
  trackEvent('balance_topup_method_select', {
    topup_amount: toIntegerOrNull(topupAmount),
    payment_method: toStringOrNull(paymentMethod),
  });

const listingCreateStart = ({ listingCreationId, isResume, accountType, listingType, searchId }) =>
  trackEvent('listing_create_start', {
    listing_creation_id: toStringOrNull(listingCreationId),
    is_resume: toBooleanOrNull(isResume),
    account_type: toStringOrNull(accountType),
    listing_type: toStringOrNull(listingType),
    search_id: toStringOrNull(searchId),
  });

const listingCreateStep = ({
  listingCreationId,
  stepName,
  stepNumber,
  eventPhase,
  lastFieldTouched,
  fieldCountFilled,
  timeInSectionMs,
  accountType,
  listingType,
  categoryId,
}) =>
  trackEvent('listing_create_step', {
    listing_creation_id: toStringOrNull(listingCreationId),
    step_name: toStringOrNull(stepName),
    step_number: toIntegerOrNull(stepNumber),
    event_phase: toStringOrNull(eventPhase),
    last_field_touched: toStringOrNull(lastFieldTouched),
    field_count_filled: toIntegerOrNull(fieldCountFilled),
    time_in_section_ms: toIntegerOrNull(timeInSectionMs),
    account_type: toStringOrNull(accountType),
    listing_type: toStringOrNull(listingType),
    category_id: toStringOrNull(categoryId),
  });

const listingSaveDraft = ({ listingCreationId, listingId, lastCompletedStep, accountType, listingType }) =>
  trackEvent('listing_save_draft', {
    listing_creation_id: toStringOrNull(listingCreationId),
    listing_id: toStringOrNull(listingId),
    last_completed_step: toStringOrNull(lastCompletedStep),
    account_type: toStringOrNull(accountType),
    listing_type: toStringOrNull(listingType),
  });

const shopCreateStart = (userId) => trackEvent('shop_create_start', { user_id: toStringOrNull(userId) });

const shopCreateSubmit = ({ shopId, orgType, hasWarrantyPolicy, auctionEnabled }) =>
  trackEvent('shop_create_submit', {
    shop_id: toStringOrNull(shopId),
    org_type: toStringOrNull(orgType),
    has_warranty_policy: toBooleanOrNull(hasWarrantyPolicy),
    auction_enabled: toBooleanOrNull(auctionEnabled),
  });

const shopEdit = ({ shopId, fieldEdited = null }) =>
  trackEvent('shop_edit', {
    shop_id: toStringOrNull(shopId),
    field_edited: toStringOrNull(fieldEdited),
  });
const financesTabSwitch = (tab) => trackEvent('finances_tab_switch', { tab: toStringOrNull(tab) });
const transactionHistoryView = (accountType) =>
  trackEvent('transaction_history_view', { account_type: toStringOrNull(accountType) });
const invoiceRequest = (amount) => trackEvent('invoice_request', { amount: toIntegerOrNull(amount) });
const messageThreadOpen = () => trackEvent('message_thread_open');
const messageSend = () => trackEvent('message_send');
const messageFilterSwitch = () => trackEvent('message_filter_switch');
const offersTabSwitch = ({ tab, side }) =>
  trackEvent('offers_tab_switch', {
    tab: toStringOrNull(tab),
    side: toStringOrNull(side),
  });

const profileSectionView = (section, accountType) =>
  trackEvent('profile_section_view', {
    section: toStringOrNull(section),
    account_type: toStringOrNull(accountType),
  });

const orderView = ({ side, orderId, orderStatus, orderAmount }) =>
  trackEvent('order_view', {
    side: toStringOrNull(side),
    order_id: toStringOrNull(orderId),
    order_status: toStringOrNull(orderStatus),
    order_amount: toIntegerOrNull(orderAmount),
  });

const ordersFilter = ({ side, filterType, statusFilter }) =>
  trackEvent('orders_filter', {
    side: toStringOrNull(side),
    filter_type: toStringOrNull(filterType),
    status_filter: toStringOrNull(statusFilter),
  });

const bankAccountAdd = () => trackEvent('bank_account_add');
const bankAccountDelete = () => trackEvent('bank_account_delete');

const addressAdd = (addressType) => trackEvent('address_add', { address_type: toStringOrNull(addressType) });

const myAdsTabSwitch = ({ tab, listingCount }) =>
  trackEvent('my_ads_tab_switch', {
    tab: toStringOrNull(tab),
    listing_count: toIntegerOrNull(listingCount),
  });

const listingEdit = ({ listingId, fieldEdited, categoryId }) =>
  trackEvent('listing_edit', {
    listing_id: toStringOrNull(listingId),
    field_edited: toStringOrNull(fieldEdited),
    category_id: toStringOrNull(categoryId),
  });

const listingStockUpdate = ({ listingId, stockDelta, newStock }) =>
  trackEvent('listing_stock_update', {
    listing_id: toStringOrNull(listingId),
    stock_delta: toIntegerOrNull(stockDelta),
    new_stock: toIntegerOrNull(newStock),
  });

const listingSellConfigToggle = ({ listingId, configType, enabled }) =>
  trackEvent('listing_sell_config_toggle', {
    listing_id: toStringOrNull(listingId),
    config_type: toStringOrNull(configType),
    enabled: toBooleanOrNull(enabled),
  });

const listingDeactivate = ({ listingId, categoryId, daysActive }) =>
  trackEvent('listing_deactivate', {
    listing_id: toStringOrNull(listingId),
    category_id: toStringOrNull(categoryId),
    days_active: toIntegerOrNull(daysActive),
  });

const listingReactivate = ({ listingId, categoryId }) =>
  trackEvent('listing_reactivate', {
    listing_id: toStringOrNull(listingId),
    category_id: toStringOrNull(categoryId),
  });

const listingDelete = ({ listingId, daysActive, categoryId }) =>
  trackEvent('listing_delete', {
    listing_id: toStringOrNull(listingId),
    days_active: toIntegerOrNull(daysActive),
    category_id: toStringOrNull(categoryId),
  });

const listingAnalyticsView = (listingId) => trackEvent('listing_analytics_view', { listing_id: toStringOrNull(listingId) });

const headerNavClick = (navItem) => trackEvent('header_nav_click', { nav_item: toStringOrNull(navItem) });

const removeFromWishlist = (product) => {
  const item = makeGa4ProductItem(product);

  return trackEvent('remove_from_wishlist', {
    item_id: item.item_id,
    items: [item],
    category_id: item.category_id,
  });
};

export const useGa4Context = () => {
  const context = useContext(Ga4Context);
  if (!context) throw new Error('useGa4Context must be used within Ga4Provider');

  return context;
};

export const useGa4Identity = () => {
  const context = useContext(Ga4IdentityContext);
  if (!context) throw new Error('useGa4Identity must be used within Ga4Provider');

  return context;
};

export const Ga4Provider = ({ children }) => {
  const [identity, setIdentity] = useState(EmptyIdentity);

  useEffect(() => {
    Promise.all([getClientId(), getSessionId()])
      .then(([clientId, sessionId]) => setIdentity({ clientId, sessionId }))
      .catch(() => setIdentity(EmptyIdentity));
  }, []);

  const analytics = useMemo(
    () => ({
      visibilityUpgradeView,
      visibilityUpgradeSelect,
      balanceTopupStart,
      balanceTopupMethodSelect,
      listingCreateStart,
      listingCreateStep,
      listingSaveDraft,
      shopCreateStart,
      shopCreateSubmit,
      shopEdit,
      financesTabSwitch,
      transactionHistoryView,
      invoiceRequest,
      messageThreadOpen,
      messageSend,
      messageFilterSwitch,
      offersTabSwitch,
      profileSectionView,
      orderView,
      ordersFilter,
      bankAccountAdd,
      bankAccountDelete,
      addressAdd,
      myAdsTabSwitch,
      listingEdit,
      listingStockUpdate,
      listingSellConfigToggle,
      listingDeactivate,
      listingReactivate,
      listingDelete,
      listingAnalyticsView,
      removeFromWishlist,
      headerNavClick,
    }),
    [],
  );

  return (
    <Ga4Context.Provider value={analytics}>
      <Ga4IdentityContext.Provider value={identity}>{children}</Ga4IdentityContext.Provider>
    </Ga4Context.Provider>
  );
};

export default Ga4Provider;
